import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { Gauge } from 'lucide-react'
import api from '../services/api'
import CohortCheckInModal from '../components/CohortCheckInModal'
import EmailOtpModal from '../components/EmailOtpModal'
import { saveAuth, getToken, clearAuth } from '../utils/userData'
import { API_ENDPOINTS, ROUTES, COHORT_DISPLAY_NAMES } from '../utils/constants'
import { formatSessionDate, formatSessionTime } from '../utils/helpers'

const scoreLabels = {
  energyLevel: { label: 'Energy Level', emoji: '⚡', color: 'from-yellow-400 to-orange-500' },
  stressLevel: { label: 'Stress Level', emoji: <Gauge className="inline w-10 h-10" />, color: 'from-red-400 to-pink-500' },
  emotionalState: { label: 'Emotional State', emoji: '💜', color: 'from-purple-400 to-indigo-500' },
  physicalState: { label: 'Physical State', emoji: '💪', color: 'from-green-400 to-teal-500' },
}

const SessionCheckIn = ({ programSlug = 'build3-cohort-7' }) => {
  const { sessionNumber } = useParams()
  const navigate = useNavigate()
  const sessionNum = parseInt(sessionNumber)
  const cohortDisplayName = COHORT_DISPLAY_NAMES[programSlug] || 'Build3 Energizer Sessions'
  
  const [loading, setLoading] = useState(true)
  const [session, setSession] = useState(null)
  const [userSignup, setUserSignup] = useState(null)
  const [checkIns, setCheckIns] = useState([])
  const [checkInModal, setCheckInModal] = useState({ isOpen: false, type: null })
  const [emailModal, setEmailModal] = useState({ isOpen: false })
  const [pendingCheckIn, setPendingCheckIn] = useState(null)

  useEffect(() => {
    checkAuthAndFetchData()
  }, [sessionNum])

  const checkAuthAndFetchData = async () => {
    setLoading(true)
    
    try {
      // Fetch session data from API
      const sessionResponse = await api.get(API_ENDPOINTS.PROGRAMS.SESSION(programSlug, sessionNum))
      if (sessionResponse.data.success) {
        setSession(sessionResponse.data.data)
      }
    } catch (err) {
      console.error('Error fetching session:', err)
      // Session not found - session will remain null
    }

    const token = getToken()

    if (token) {
      try {
        const meResponse = await api.get(API_ENDPOINTS.PROGRAMS.ENROLLMENT(programSlug))
        if (meResponse.data.success) {
          const { user, enrollment } = meResponse.data.data
          setUserSignup({
            id: user.id,
            name: user.name,
            email: user.email,
            phone: user.phone,
            emailVerified: user.emailVerified,
            ...enrollment?.data,
          })
          fetchCheckIns()
        }
      } catch {
        // Token invalid, clear storage
        clearAuth()
      }
    }
    setLoading(false)
  }

  const fetchCheckIns = async () => {
    try {
      const response = await api.get(API_ENDPOINTS.PROGRAMS.CHECK_INS(programSlug))
      if (response.data.success) {
        setCheckIns(response.data.data)
      }
    } catch (error) {
      console.error('Error fetching check-ins:', error)
    }
  }

  const hasPreEventCheckIn = (num) => checkIns.some(
    (ci) => ci.sessionNumber === num && ci.checkInType === 'pre-event'
  )
  const hasPostEventCheckIn = (num) => checkIns.some(
    (ci) => ci.sessionNumber === num && ci.checkInType === 'post-event'
  )

  const getCheckIn = (num, type) => checkIns.find(
    (ci) => ci.sessionNumber === num && ci.checkInType === type
  )

  const openCheckInModal = (type) => {
    setCheckInModal({ isOpen: true, type, sessionNumber: sessionNum })
  }

  const closeCheckInModal = () => {
    setCheckInModal({ isOpen: false, type: null, sessionNumber: null })
  }

  const handleCheckInSuccess = () => {
    // Refresh check-ins after successful submission
    const token = getToken()
    if (token) {
      fetchCheckIns()
    }
  }

  // Handle unauthenticated check-in completion
  const handleUnauthenticatedComplete = (checkInData) => {
    // Store pending check-in in localStorage
    const pendingCheckIns = JSON.parse(localStorage.getItem('pendingCohortCheckIns') || '[]')
    const newCheckIn = {
      ...checkInData,
      sessionNumber: sessionNum,
      checkInType: checkInModal.type,
      timestamp: new Date().toISOString(),
    }
    
    // Check if there's already a pending check-in for this session/type
    const existingIndex = pendingCheckIns.findIndex(
      ci => ci.sessionNumber === sessionNum && ci.checkInType === checkInModal.type
    )
    
    if (existingIndex >= 0) {
      pendingCheckIns[existingIndex] = newCheckIn
    } else {
      pendingCheckIns.push(newCheckIn)
    }
    
    localStorage.setItem('pendingCohortCheckIns', JSON.stringify(pendingCheckIns))
    setPendingCheckIn(newCheckIn)
    
    // Close check-in modal and open email modal
    closeCheckInModal()
    setEmailModal({ isOpen: true })
  }

  const closeEmailModal = () => {
    setEmailModal({ isOpen: false })
  }

  // Check if we have a pending check-in for this session
  const getPendingCheckIn = (type) => {
    const pendingCheckIns = JSON.parse(localStorage.getItem('pendingCohortCheckIns') || '[]')
    return pendingCheckIns.find(ci => ci.sessionNumber === sessionNum && ci.checkInType === type)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="w-16 h-16 border-4 border-primary-600 border-t-transparent rounded-full"
        />
      </div>
    )
  }

  if (!session) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-purple-50 to-pink-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md text-center">
          <div className="text-6xl mb-4">🔍</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Session Not Found</h2>
          <p className="text-gray-600 mb-6">
            Session {sessionNumber} doesn't exist or hasn't been scheduled yet.
          </p>
          <Link
            to={ROUTES.COHORT_DASHBOARD(programSlug)}
            className="inline-block px-6 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors"
          >
            Go to Dashboard
          </Link>
        </div>
      </div>
    )
  }

  const hasBothCheckIns = hasPreEventCheckIn(sessionNum) && hasPostEventCheckIn(sessionNum)
  const hasPendingPre = getPendingCheckIn('pre-event')
  const hasPendingPost = getPendingCheckIn('post-event')

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-purple-50 to-pink-50">
      <div className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 flex justify-center"
          >
            <Link to="/">
              <img
                src="/logo.png"
                alt="Wellness Loop Logo"
                className="h-16 w-auto"
              />
            </Link>
          </motion.div>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
              {cohortDisplayName}
            </h1>
            <p className="text-lg text-gray-600">
              Session Check-In
            </p>
          </motion.div>

          {/* Session Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-br from-pink-50 via-purple-50 to-primary-50 rounded-3xl shadow-xl p-8 border-2 border-purple-200"
          >
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div className="flex-1">
                <div className="mb-2">
                  <span className="text-sm font-semibold text-purple-700 uppercase tracking-wide">
                    {cohortDisplayName}
                  </span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                  Session {session.number}: {session.title}
                </h2>
                <p className="text-lg text-gray-700 mb-4 italic">
                  {session.subtitle}
                </p>
                <div className="flex flex-wrap items-center gap-4 text-gray-600 mb-6">
                  <div className="flex items-center">
                    <span className="text-xl mr-2">📅</span>
                    <span className="font-semibold">{formatSessionDate(session.sessionDateTime)}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-xl mr-2">⏰</span>
                    <span className="font-semibold">{formatSessionTime(session.sessionDateTime, session.endDateTime)}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-xl mr-2">📍</span>
                    <span className="font-semibold">{session.location}</span>
                  </div>
                  {session.pdf && (
                    <a
                      href={session.pdf.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center text-purple-600 hover:text-purple-800 transition-colors"
                    >
                      <span className="text-xl mr-2">📄</span>
                      <span className="font-semibold underline">{session.pdf.title || 'Session PDF'}</span>
                    </a>
                  )}
                </div>

                {/* Check-In Buttons */}
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => openCheckInModal('pre-event')}
                    className={`px-6 py-3 rounded-xl font-semibold transition-all ${
                      hasPreEventCheckIn(sessionNum)
                        ? 'bg-green-100 text-green-700 border-2 border-green-300'
                        : hasPendingPre
                        ? 'bg-yellow-100 text-yellow-700 border-2 border-yellow-300'
                        : 'bg-primary-600 text-white hover:bg-primary-700'
                    }`}
                  >
                    {hasPreEventCheckIn(sessionNum) 
                      ? '✓ Pre-Event Check-In Complete' 
                      : hasPendingPre
                      ? '⏳ Pre-Event Pending Login'
                      : 'Pre-Event Check-In'}
                  </button>
                  <button
                    onClick={() => openCheckInModal('post-event')}
                    className={`px-6 py-3 rounded-xl font-semibold transition-all ${
                      hasPostEventCheckIn(sessionNum)
                        ? 'bg-green-100 text-green-700 border-2 border-green-300'
                        : hasPendingPost
                        ? 'bg-yellow-100 text-yellow-700 border-2 border-yellow-300'
                        : 'bg-purple-600 text-white hover:bg-purple-700'
                    }`}
                  >
                    {hasPostEventCheckIn(sessionNum) 
                      ? '✓ Post-Event Check-In Complete' 
                      : hasPendingPost
                      ? '⏳ Post-Event Pending Login'
                      : 'Post-Event Check-In'}
                  </button>
                </div>

                {/* Pending check-in message */}
                {(hasPendingPre || hasPendingPost) && !userSignup && (
                  <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-sm text-yellow-800">
                      <span className="font-medium">Note:</span> You have pending check-ins. 
                      <button 
                        onClick={() => setEmailModal({ isOpen: true })}
                        className="ml-1 text-yellow-900 underline hover:no-underline"
                      >
                        Sign in or sign up
                      </button> to save them.
                    </p>
                  </div>
                )}
              </div>

              {/* Facilitator Card */}
              <div className="bg-white/60 rounded-2xl p-4 text-center min-w-[180px] border border-purple-100">
                <div className="text-sm text-purple-600 font-semibold uppercase tracking-wider mb-1">Facilitator</div>
                <div className="text-xl font-bold text-gray-900 mb-0.5">{session.facilitator}</div>
                <div className="text-sm text-gray-600">{session.facilitatorTitle}</div>
              </div>
            </div>

            {/* Score Comparison - Show when both check-ins are done */}
            {userSignup && hasBothCheckIns && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="mt-6 pt-6 border-t border-purple-200"
              >
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <span className="mr-2">📊</span>
                  Your Session Results
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {Object.entries(scoreLabels).map(([key, { label, emoji }]) => {
                    const preCheckIn = getCheckIn(sessionNum, 'pre-event')
                    const postCheckIn = getCheckIn(sessionNum, 'post-event')
                    const preValue = preCheckIn?.[key] || 0
                    const postValue = postCheckIn?.[key] || 0
                    const diff = postValue - preValue
                    const isStress = key === 'stressLevel'
                    const isImproved = isStress ? diff < 0 : diff > 0
                    const isDeclined = isStress ? diff > 0 : diff < 0

                    return (
                      <div
                        key={key}
                        className="bg-white/80 backdrop-blur rounded-xl p-4 shadow-sm"
                      >
                        <div className="flex items-center justify-center mb-2">
                          <span className="text-2xl">{emoji}</span>
                        </div>
                        <div className="text-center text-xs font-medium text-gray-600 mb-2">{label}</div>
                        <div className="flex items-center justify-center gap-2">
                          <span className="text-lg font-semibold text-gray-500">{preValue}</span>
                          <span className="text-gray-400">→</span>
                          <span className="text-lg font-bold text-gray-800">{postValue}</span>
                        </div>
                        <div className="text-center mt-2">
                          <span
                            className={`inline-block px-2 py-1 rounded-full text-xs font-bold ${
                              isImproved
                                ? 'bg-green-100 text-green-700'
                                : isDeclined
                                ? 'bg-red-100 text-red-700'
                                : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {diff > 0 ? '+' : ''}{diff}
                            {isImproved && ' ↑'}
                            {isDeclined && ' ↓'}
                            {diff === 0 && ' →'}
                          </span>
                        </div>
                      </div>
                    )
                  })}
                </div>

                {/* Summary message */}
                {(() => {
                  const preCheckIn = getCheckIn(sessionNum, 'pre-event')
                  const postCheckIn = getCheckIn(sessionNum, 'post-event')
                  const improvements = Object.keys(scoreLabels).filter(key => {
                    const diff = (postCheckIn?.[key] || 0) - (preCheckIn?.[key] || 0)
                    return key === 'stressLevel' ? diff < 0 : diff > 0
                  })
                  
                  if (improvements.length >= 3) {
                    return (
                      <div className="mt-4 flex items-center text-green-700 bg-green-100/80 rounded-lg px-4 py-3">
                        <span className="text-xl mr-2">🎉</span>
                        <span className="font-medium">Amazing! You showed improvement in {improvements.length} wellness areas this session!</span>
                      </div>
                    )
                  } else if (improvements.length > 0) {
                    return (
                      <div className="mt-4 flex items-center text-purple-700 bg-purple-100/80 rounded-lg px-4 py-3">
                        <span className="text-xl mr-2">✨</span>
                        <span className="font-medium">Great progress! You improved in {improvements.length} area{improvements.length > 1 ? 's' : ''}.</span>
                      </div>
                    )
                  } else {
                    return (
                      <div className="mt-4 flex items-center text-gray-600 bg-gray-100/80 rounded-lg px-4 py-3">
                        <span className="text-xl mr-2">💭</span>
                        <span className="font-medium">Every session is a step forward. Keep up the wellness journey!</span>
                      </div>
                    )
                  }
                })()}
              </motion.div>
            )}
          </motion.div>

          {/* Navigation Links */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="mt-8 flex flex-wrap justify-center gap-4"
          >
            <Link
              to={ROUTES.COHORT_DASHBOARD(programSlug)}
              className="px-6 py-3 bg-white text-primary-600 font-semibold rounded-xl shadow-md hover:shadow-lg transition-all border border-primary-200"
            >
              View Full Dashboard
            </Link>
            {!userSignup && (
              <button
                onClick={() => setEmailModal({ isOpen: true })}
                className="px-6 py-3 bg-gradient-to-r from-primary-600 to-purple-600 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all"
              >
                Sign In / Sign Up
              </button>
            )}
          </motion.div>

          {/* Footer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-center mt-8 text-gray-500"
          >
            <p>Build3 Energizer Sessions</p>
            <p className="text-sm mt-2">Powered by Wellness Loop</p>
          </motion.div>
        </div>
      </div>

      {/* Check-In Modal */}
      {checkInModal.isOpen && (
        <CohortCheckInModal
          isOpen={checkInModal.isOpen}
          onClose={closeCheckInModal}
          sessionNumber={checkInModal.sessionNumber}
          checkInType={checkInModal.type}
          onSuccess={handleCheckInSuccess}
          isAuthenticated={!!userSignup}
          onUnauthenticatedComplete={handleUnauthenticatedComplete}
          programSlug={programSlug}
        />
      )}

      {/* Email Modal for Login/Signup */}
      <EmailOtpModal
        isOpen={emailModal.isOpen}
        onClose={closeEmailModal}
        title={cohortDisplayName}
        emoji="🧘"
        emailDescription="Energizer Sessions - Enter your email to continue"
        emailHint="If you're already signed up, we'll send you a verification code. If not, we'll help you get started!"
        requestOtp={async (email) => {
          const response = await api.post('/api/auth/otp', {
            email,
            programSlug,
          })
          return {
            success: response.data.success,
            userExists: response.data.data?.userExists,
            code: response.data.data?.code,
          }
        }}
        verifyOtp={async (email, code) => {
          const response = await api.post('/api/auth/verify-otp', {
            email,
            code,
            programSlug,
          })
          return response.data
        }}
        onSuccess={(data) => {
          const { token } = data
          saveAuth(token, null)  // Just save token, user data will be fetched
          // Refresh the page data after login
          checkAuthAndFetchData()
        }}
        onUserNotFound={(email) => {
          navigate(ROUTES.COHORT_SIGNUP(programSlug), { state: { prefillEmail: email } })
        }}
      />
    </div>
  )
}

export default SessionCheckIn

